import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Toaster as SonnerToaster } from 'sonner';
import { Analytics } from '@vercel/analytics/react';
import { ArrowLeft, Languages, Sun, Moon, Palette, ChevronDown, Check, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { translations } from '@/lib/translations';
import '@/App.css';

const ACCENT_OPTIONS = [
  { id: 'amber', label: 'Ambra', color: 'hsl(38, 92%, 50%)' },
  { id: 'emerald', label: 'Smeraldo', color: 'hsl(160, 84%, 39%)' },
  { id: 'sky', label: 'Cielo', color: 'hsl(199, 89%, 48%)' },
  { id: 'violet', label: 'Viola', color: 'hsl(263, 70%, 50%)' },
  { id: 'rose', label: 'Rosa', color: 'hsl(350, 89%, 60%)' },
  { id: 'black', label: 'Nero', color: 'hsl(0, 0%, 18%)' },
];

const LANG_OPTIONS = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function PrivacyPage() {
  const [theme, setTheme] = useState(() => localStorage.getItem('dnd-theme') || 'light');
  const [accent, setAccent] = useState(() => localStorage.getItem('dnd-accent') || 'sky');
  const [lang, setLang] = useState(() => localStorage.getItem('dnd-lang') || 'it');
  const t = translations[lang] || translations.it;

  useEffect(() => {
    localStorage.setItem('dnd-theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('dnd-accent', accent);
    document.documentElement.setAttribute('data-accent', accent);
  }, [accent]);

  useEffect(() => {
    localStorage.setItem('dnd-lang', lang);
  }, [lang]);

  useEffect(() => {
    document.title = `${t.privacyTitle || 'Privacy'} | CoCo`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', t.privacyMetaDescription || t.privacyIntro || '');
    }
    return () => {
      document.title = 'CoCo | Combat Companion';
    };
  }, [t]);

  const toggleTheme = () => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  const sections = t.privacySections || translations.it.privacySections;

  return (
    <div className="coco-v2 min-h-[100dvh]">
      <Analytics />
      <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col">
        <SonnerToaster theme={theme} />
        <header className="sticky top-0 z-50 glass coco-v2-header pt-[max(0.25rem,env(safe-area-inset-top))]">
          <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" asChild>
                <Link to="/" aria-label={t.backToHome || 'Torna alla home'}>
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              </Button>
              <div className="flex items-center gap-2 min-w-0">
                <Shield className="w-5 h-5 accent-text shrink-0" />
                <h1 className="font-semibold tracking-tight text-base sm:text-lg truncate">
                  {t.privacyTitle || 'Informativa sulla privacy'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-9 sm:h-8 gap-1.5 text-muted-foreground font-normal px-2 sm:px-3">
                    <Languages className="w-4 h-4 shrink-0" />
                    <span className="text-xs font-mono uppercase">{lang}</span>
                    <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {LANG_OPTIONS.map((opt) => (
                    <DropdownMenuItem key={opt.code} onClick={() => setLang(opt.code)} className={lang === opt.code ? 'bg-accent' : ''}>
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label={t.themeLabel || 'Tema'}>
                    <Palette className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{t.themeLabel || 'Tema'}</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => theme !== 'light' && toggleTheme()}>
                    <Sun className="w-4 h-4 mr-2" />
                    {t.themeLight || 'Chiaro'}
                    {theme === 'light' && <Check className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => theme !== 'dark' && toggleTheme()}>
                    <Moon className="w-4 h-4 mr-2" />
                    {t.themeDark || 'Scuro'}
                    {theme === 'dark' && <Check className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{t.accentLabel || 'Accento'}</DropdownMenuLabel>
                  <div className="px-2 py-2 grid grid-cols-3 gap-2 justify-items-center">
                    {ACCENT_OPTIONS.map(({ id, color, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setAccent(id)}
                        className={`w-8 h-8 rounded-full border-2 transition-all shrink-0 ${accent === id ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-offset-background ring-muted-foreground' : 'border-transparent hover:scale-105'}`}
                        style={{ backgroundColor: color }}
                        title={label}
                        aria-label={id}
                        aria-pressed={accent === id}
                      />
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-3xl mx-auto min-w-0 px-4 sm:px-6 py-6 pb-[max(2rem,env(safe-area-inset-bottom))]">
          <p className="text-sm text-muted-foreground mb-6">{t.privacyLastUpdated}</p>
          <p className="text-[15px] text-muted-foreground leading-7 mb-6">{t.privacyIntro}</p>

          <div className="space-y-3 sm:space-y-4">
            {sections.map((section) => (
              <Card key={section.title} className="coco-v2-card border accent-border-30">
                <CardContent className="p-4 sm:p-5">
                  <h2 className="font-semibold tracking-tight text-base sm:text-lg leading-6">{section.title}</h2>
                  <p className="text-[15px] text-muted-foreground leading-7 mt-2 whitespace-pre-line">{section.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            <Link to="/" className="underline underline-offset-2 hover:text-foreground transition-colors">
              {t.backToHome || 'Torna alla home'}
            </Link>
          </p>
        </main>
      </div>
    </div>
  );
}
